<template>
  <div class="flex flex-col h-full text-xs text-white">
    <!-- Search bar -->
    <div class="flex-shrink-0 pb-2">
      <HelpSearch @select="store.openDocument($event)" />
    </div>

    <!-- Body: sidebar + content -->
    <div class="flex flex-1 min-h-0 gap-3">
      <HelpSidebar @select="store.openDocument($event)" />

      <!-- Main content area -->
      <div class="flex-1 overflow-y-auto min-w-0">
        <!-- Loading -->
        <div v-if="store.loading" class="flex items-center justify-center h-32 text-slate-400">
          <svg class="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          Loading...
        </div>

        <!-- Error -->
        <div v-else-if="store.error" class="p-4 text-red-400">
          <p class="font-semibold">Failed to load documentation</p>
          <p class="mt-1 text-slate-400">{{ store.error }}</p>
          <p class="mt-2 text-slate-500">Make sure the docs server is reachable at <code class="text-ui-active">{{ docsBaseUrl }}</code></p>
        </div>

        <!-- No document selected -->
        <div v-else-if="!store.currentDoc" class="p-4 text-slate-400">
          <p class="text-base font-semibold text-white mb-2">haspdesigner Documentation</p>
          <p>Select a topic from the sidebar or use the search bar to find help.</p>
        </div>

        <!-- Document content -->
        <div v-else class="pr-2">
          <!-- Breadcrumb -->
          <div class="flex items-center gap-1 text-slate-400 mb-3 text-xs">
            <span>{{ store.currentDoc.category }}</span>
            <span>›</span>
            <span class="text-white">{{ store.currentDoc.title }}</span>
          </div>

          <!-- Related articles -->
          <div v-if="relatedDocs.length" class="mb-3 flex flex-wrap gap-1">
            <span class="text-slate-400">Related:</span>
            <button
              v-for="rel in relatedDocs"
              :key="rel.id"
              class="text-cyan-400 hover:underline"
              @click="store.openDocument(rel.id)"
            >{{ rel.title }}</button>
          </div>

          <!-- Rendered markdown -->
          <div
            class="doc-content"
            v-html="store.currentDoc.html"
            @click="handleContentClick"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useDocumentationStore } from '../../stores/documentationStore.js'
import { DocumentationService } from '../../documentation/DocumentationService.js'
import HelpSearch from './HelpSearch.vue'
import HelpSidebar from './HelpSidebar.vue'

const store = useDocumentationStore()

const docsBaseUrl = computed(() => DocumentationService.baseUrl)

const relatedDocs = computed(() => {
  if (!store.currentDoc?.related) return []
  const ids = Array.isArray(store.currentDoc.related) ? store.currentDoc.related : [store.currentDoc.related]
  return ids.map(id => store.index.find(e => e.id === id)).filter(Boolean)
})

function handleContentClick(e) {
  const link = e.target.closest('[data-help-link]')
  if (link) {
    e.preventDefault()
    store.openDocument(link.dataset.helpLink)
  }
}

onMounted(() => {
  store.loadIndex()
})
</script>

<style scoped>
/* Markdown content styles — scoped to doc-content */
.doc-content :deep(h1) { font-size: 1.1rem; font-weight: 700; margin: 0.75rem 0 0.5rem; color: #f1f5f9; }
.doc-content :deep(h2) { font-size: 0.95rem; font-weight: 600; margin: 0.75rem 0 0.4rem; color: #e2e8f0; border-bottom: 1px solid rgba(255,255,255,0.15); padding-bottom: 0.2rem; }
.doc-content :deep(h3) { font-size: 0.85rem; font-weight: 600; margin: 0.6rem 0 0.3rem; color: #cbd5e1; }
.doc-content :deep(p) { margin: 0.4rem 0; line-height: 1.6; color: #cbd5e1; }
.doc-content :deep(ul) { list-style: disc; padding-left: 1.25rem; margin: 0.4rem 0; color: #cbd5e1; }
.doc-content :deep(ol) { list-style: decimal; padding-left: 1.25rem; margin: 0.4rem 0; color: #cbd5e1; }
.doc-content :deep(li) { margin: 0.15rem 0; line-height: 1.5; }
.doc-content :deep(code) { background: var(--ui-bg-input); color: var(--ui-active); padding: 0.1rem 0.3rem; border-radius: 0.2rem; font-family: monospace; font-size: 0.8rem; }
.doc-content :deep(pre) { background: var(--ui-bg-input); padding: 0.75rem; border-radius: 0.375rem; overflow-x: auto; margin: 0.5rem 0; border: 1px solid rgba(255,255,255,0.15); }
.doc-content :deep(pre code) { background: none; padding: 0; color: #e2e8f0; font-size: 0.75rem; }
.doc-content :deep(table) { border-collapse: collapse; width: 100%; margin: 0.5rem 0; }
.doc-content :deep(th) { background: var(--ui-bg-input); color: #94a3b8; padding: 0.3rem 0.5rem; text-align: left; border: 1px solid rgba(255,255,255,0.15); }
.doc-content :deep(td) { padding: 0.25rem 0.5rem; border: 1px solid rgba(255,255,255,0.15); color: #cbd5e1; }
.doc-content :deep(tr:nth-child(even)) { background: rgba(255,255,255,0.04); }
.doc-content :deep(blockquote) { border-left: 3px solid rgba(255,255,255,0.2); padding-left: 0.75rem; color: #94a3b8; margin: 0.4rem 0; }
.doc-content :deep(a) { color: var(--ui-active); text-decoration: underline; }
.doc-content :deep(a:hover) { opacity: 0.8; }
.doc-content :deep(hr) { border: none; border-top: 1px solid rgba(255,255,255,0.15); margin: 0.75rem 0; }
.doc-content :deep(.doc-container) { padding: 0.5rem 0.75rem; border-radius: 0.375rem; margin: 0.5rem 0; border-left: 3px solid; }
.doc-content :deep(.doc-container--tip) { background: #0f4c3f; border-color: #10b981; color: #a7f3d0; }
.doc-content :deep(.doc-container--note) { background: #0c3058; border-color: #3b82f6; color: #93c5fd; }
.doc-content :deep(.doc-container--warning) { background: #451a03; border-color: #f59e0b; color: #fcd34d; }
.doc-content :deep(.doc-container--danger) { background: #450a0a; border-color: #ef4444; color: #fca5a5; }
</style>
